// Lens domain: the shapes every Lens unit (server, web, proof) agrees on, and the
// pure rules that both the server and the browser must compute identically.
// Money is always integer cents. Timestamps are ISO 8601 strings.

import type { NsResellerDirectory } from "@phoneware/shared";
export type { NsResellerDirectory } from "@phoneware/shared";
export { validateNetsapiensAccountsDoc, NETSAPIENS_ACCOUNTS_DOC_PATH } from "@phoneware/shared";
import type { AutotaskCategoryLink, AutotaskOwnerSeed, CustomerBilling } from "./customers-v2.js";
import type { CatalogCodeReport } from "./catalog-v2.js";
import type { Via } from "./via.js";
import type { OrderKind, QuoteSource } from "./mac.js";
import type { OrderTicket, OrderTicketLive } from "./tickets.js";
import type { BillingLineValue, OrderLineNumber } from "./billing-requirements.js";
import type { ActingAsView } from "./act-as.js";

export type Cents = number;
export type Iso = string;

// ---------- dealers, users, roles ----------

/** Agent dealers quote Phoneware's list price down to a floor; wholesale dealers set their own sell price over a buy price. */
export type DealerModel = "agent" | "wholesale";

export interface DealerBrand {
 /** Hex colour for the dealer's QuoteLink page and PDF accents. */
 color: string;
 /** Logo as a data URL (PNG or SVG), or null for a text mark. */
 logoDataUrl: string | null;
}

export interface Dealer {
 id: string;
 name: string;
 model: DealerModel;
 city: string | null;
 /** Optional exact NS reseller key. Lens-only dealers have no NS association. */
 nsReseller: string | null;
 /** Last complete directory read that observed this key; never grants Lens access. */
 nsResellerVerifiedAt: Iso | null;
 brand: DealerBrand;
 /**
  * The Autotask resource that owns every company this dealer creates (decided 2026-10-14: per dealer, set by ops).
  * Autotask requires a human owner and refuses an API-only user. null until ops choose one; the dealer cannot add
  * customers while it is null.
  */
  autotaskOwner: AutotaskOwner | null;
  createdAt: Iso;
  autotaskCategory?: AutotaskCategoryLink | null;
  autotaskOwnerSeed?: AutotaskOwnerSeed | null;
  /**
   * GET /api/dealers only: true on the house dealer, the one lens_settings/house_dealer names (Phoneware's own sales).
   * Not stored on the dealer; absent on every other dealer.
   */
  house?: boolean;
}

/** An active, licensed (not API-only) Autotask resource that can own a company. */
export interface AutotaskOwner {
 resourceId: number;
 name: string;
 email: string | null;
}

export interface NsDirectoryStatus {
 directory: NsResellerDirectory | null;
 lastAttempt: { finishedAt: Iso; error: string | null } | null;
 entries: Array<{
  reseller: string;
  dealerIds: string[];
  verifiedDealerId: string | null;
 }>;
 issues: Array<{
  kind: "missing" | "duplicate" | "unverified" | "unknown_reseller" | "invalid_claim";
  reseller: string;
  dealerIds: string[];
 }>;
 /** Coverage of the timestamped directory, or null before a successful read. */
 coverageComplete: boolean | null;
}

export interface NsDealerSyncResult {
 created: number;
 verified: number;
 status: NsDirectoryStatus;
}

export * from "./access.js";
import type { Role, DealerRole, AdminTier, Permission, AuditAction, WriteScope } from "./access.js";
export type { Role, DealerRole, AdminTier, Permission, AuditAction, WriteScope };

/** `all`: every customer of the dealer. `own`: only customers the user added or was assigned. */
export type UserScope = "all" | "own";
export type UserStatus = "invited" | "active" | "disabled";

export interface UserLastSignIn {
  provider: string;
  at: Iso;
  emailVerified: boolean;
  secondFactor?: string | null;
}

export interface BaseLensUser {
  /** Lower-cased email. The document id. */
  email: string;
  name: string;
  status: UserStatus;
  /** Identity Platform uid, set on first sign-in. */
  uid: string | null;
  invitedBy: string | null;
  createdAt: Iso;
  lastSignIn?: UserLastSignIn | null;
}

/** Admin users see every dealer: dealerId is null, scope is "all", role is AdminTier. */
export interface AdminLensUser extends BaseLensUser {
  dealerId: null;
  role: AdminTier;
  scope: "all";
}

/** Dealer users belong to a specific dealer: dealerId is a string, role is DealerRole. */
export interface DealerLensUser extends BaseLensUser {
  dealerId: string;
  role: DealerRole;
  scope: UserScope;
}

export type LensUser = AdminLensUser | DealerLensUser;

export interface Me {
  user: LensUser;
  dealer: Dealer | null;
  permissions?: Permission[];
 actingAs?: ActingAsView | null;
}

// ---------- catalog ----------

export type Billing = "monthly" | "one_time";

export interface OneBillMapping {
 /** OneBill product code and the price plan Lens orders against. */
 productCode: string;
 pricePlanId: string;
}

export interface AutotaskMapping {
 /** Products are defined in Autotask (decided 2026-10-14). Services bill monthly; products are one-time. */
 kind: "service" | "product";
 id: number;
}

/**
 * The provider billing calendar verified by an operator for the active OneBill
 * connection. This is durable operational fact, not configuration inferred from
 * an offset or process environment.
 */
export interface OneBillProviderCalendar {
 host: string;
 tenantId: string;
 timeZone: string;
 verifiedAt: string;
 verifiedBy: string;
 verificationSource: string;
}
const ONEBILL_ORIGIN = /^(https?):\/\/(\[[0-9a-f:.]+\]|[a-z0-9.-]+)(?::(\d+))?\/?$/i;


function normalizeOneBillHost(host: string): string {
 if (typeof host !== "string" || host.trim() === "") {
  throw new Error("OneBill provider calendar host must be a non-empty URL.");
 }

 const parts = ONEBILL_ORIGIN.exec(host.trim());
 if (!parts) {
  throw new Error("OneBill provider calendar host must be an HTTP(S) origin URL.");
 }
 const protocol = parts[1].toLowerCase();
 const authority = parts[2].toLowerCase();
 const port = parts[3] === undefined ? null : Number(parts[3]);
 if (port !== null && (!Number.isInteger(port) || port < 1 || port > 65535)) {
  throw new Error("OneBill provider calendar host has an invalid port.");
 }
 const defaultPort = protocol === "https" ? 443 : 80;
 return `${protocol}://${authority}${port === null || port === defaultPort ? "" : `:${port}`}`;
}

function requireCalendarString(value: unknown, field: keyof OneBillProviderCalendar): string {
 if (typeof value !== "string" || value.trim() === "") {
  throw new Error(`OneBill provider calendar ${field} must be a non-empty string.`);
 }
 return value;
}

function validateOneBillTimeZone(timeZone: string): void {
 if (/^(?:UTC|[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+)+)$/.test(timeZone) === false) {
  throw new Error(`OneBill provider calendar timeZone "${timeZone}" must be a named IANA timezone.`);
 }

 try {
  new Intl.DateTimeFormat("en-US", { timeZone }).format();
 } catch {
  throw new Error(`OneBill provider calendar timeZone "${timeZone}" is not supported by this runtime.`);
 }
}

/**
 * Validates the OneBill billing calendar against the configured connection.
 * It intentionally returns the caller's record unchanged: verification data is
 * evidence, not a place to normalize or invent values.
 */
export function validateOneBillProviderCalendar(
 record: unknown,
 expected: { host: string; tenantId: string },
): OneBillProviderCalendar {
 if (typeof record !== "object" || record === null || Array.isArray(record)) {
  throw new Error("OneBill provider calendar record is required.");
 }

 const candidate = record as Record<string, unknown>;
 const host = requireCalendarString(candidate.host, "host");
 const tenantId = requireCalendarString(candidate.tenantId, "tenantId");
 const timeZone = requireCalendarString(candidate.timeZone, "timeZone");
 const verifiedAt = requireCalendarString(candidate.verifiedAt, "verifiedAt");
 requireCalendarString(candidate.verifiedBy, "verifiedBy");
 requireCalendarString(candidate.verificationSource, "verificationSource");

 if (normalizeOneBillHost(host) !== normalizeOneBillHost(expected.host)) {
  throw new Error(`OneBill provider calendar host "${host}" does not match the configured connection.`);
 }

 if (tenantId !== expected.tenantId) {
  throw new Error(`OneBill provider calendar tenantId "${tenantId}" does not match the configured connection.`);
 }

 validateOneBillTimeZone(timeZone);

 if (Number.isNaN(Date.parse(verifiedAt))) {
  throw new Error(`OneBill provider calendar verifiedAt "${verifiedAt}" must be a valid timestamp.`);
 }

 return record as OneBillProviderCalendar;
}

/**
 * A catalog entry as Lens stores it: the Autotask definition (name, description, price, cost) refreshed from
 * Autotask, plus Lens's own pricing layer (floor, wholesale buy price, dealer visibility) and the link to the
 * matching OneBill product. Only Phoneware ops see the raw record. The id is `${autotask.kind}-${autotask.id}`.
 */
export interface CatalogProduct {
 id: string;
 autotask: AutotaskMapping;
 /** Autotask's SKU or code when it has one, else "". Shown small and grey, never as the headline. */
 code: string;
 name: string;
 description: string;
 category: string;
 /** "service" maps to monthly, "product" to one_time. */
 billing: Billing;
 unit: string;
 /** Autotask unit price. */
 listPrice: Cents;
 /** Autotask unit cost, for ops only. */
 unitCost: Cents | null;
 /** Whether the item is active in Autotask. Inactive items cannot be quoted. */
 autotaskActive: boolean;
 /** When the Autotask fields were last refreshed. */
 syncedFromAutotaskAt: Iso;
 /** Lens pricing layer. Agent dealers can quote down to this without approval. Seeded at 80% of list on first import, then owned by ops. */
 floorPrice: Cents;
 /** Lens pricing layer. Phoneware's price to wholesale dealers. null until ops enter it; a wholesale dealer cannot quote the product while null. */
 wholesaleCost: Cents | null;
 /** Lens pricing layer. Whether dealers see it at all. New items start hidden until ops enable them. */
 enabled: boolean;
 /**
  * The OneBill product and price plan an order bills against. Linked by code, exact name, product key,
  * or ops review, or when the sync creates the product after ops chose `create`.
  */
 onebill: OneBillMapping | null;
 /** Ops review of the OneBill link (decided 2026-10-14). Quotable only when `state` is "linked" and `onebill` is set. */
 billingLink: BillingLink;
 updatedAt: Iso;
}

/**
 * - unreviewed: nobody has looked yet. Lens suggests candidates; the item cannot be quoted.
 * - linked: ops confirmed `onebill` is this item. Quotable (subject to enabled, active and prices).
 * - create: ops decided OneBill has no such product; the next sync apply creates it, then the state becomes linked.
 * - not_billed: ops decided this item is not billed through OneBill; it is never quotable in Lens.
 */
export type BillingLinkState = "unreviewed" | "linked" | "create" | "not_billed";

export interface BillingLink {
 state: BillingLinkState;
 via?: "ops" | "auto" | null;
 /** Email of the ops user who set the state. null while unreviewed. */
 reviewedBy: string | null;
 reviewedAt: Iso | null;
 copyFrom?: OneBillMapping | null;
 createError?: { at: Iso; by: string; message: string } | null;
}

/** A OneBill product suggested for an Autotask item, best first. Score is name similarity from 0 to 1. */
export interface OneBillCandidate {
 productCode: string;
 name: string;
 score: number;
 sameCode: boolean;
 pricePlans: Array<{ id: string; name: string; billing: Billing; amount: Cents; glCode?: string | null }>;
}
/** PUT /api/catalog/:id/billing-link. Linking checks the product and plan exist in OneBill and the plan bills the same way (monthly or one-time). */
export type BillingLinkRequest =
 | { state: "linked"; onebill: OneBillMapping }
 | { state: "create"; copyFrom: OneBillMapping }
 | { state: "not_billed" }
 | { state: "unreviewed" };

// ---------- Autotask to OneBill product sync ----------

/**
 * The sync creates in OneBill only what ops chose to create. It never deletes, and it never changes an existing
 * OneBill product or price plan: 675 migrated customers bill on those plans, and Lens orders carry their own price
 * per subscription, so a plan amount that differs from Autotask is reported, not pushed.
 * - create: ops chose `create`; apply creates the product and a price plan at the Autotask price, then links it.
 * - linked: confirmed link, plan amount equals the Autotask price.
 * - price_differs: confirmed link, plan amount differs from the Autotask price. Information only.
 * - needs_review: unreviewed. Carries suggested candidates; apply does nothing with it.
 * Items marked not_billed are left out of the plan.
 */
export type SyncAction = "create" | "linked" | "price_differs" | "needs_review";

export interface SyncItem {
 productId: string;
 autotask: AutotaskMapping;
 name: string;
 billing: Billing;
 listPrice: Cents;
 action: SyncAction;
 /** The confirmed link. null for create (until applied) and needs_review. */
 onebill: OneBillMapping | null;
 onebillPrice: Cents | null;
 /** Suggestions for needs_review, best first, at most five. Empty otherwise. */
 candidates: OneBillCandidate[];
 reason: string;
 copyFrom: (OneBillMapping & { productName: string; planName: string }) | null;
 createError: string | null;
}

export interface SyncPlan {
 generatedAt: Iso;
 items: SyncItem[];
 counts: Record<SyncAction, number>;
}

export interface SyncResult {
 appliedAt: Iso;
 /** Items created in OneBill and now linked. */
 applied: SyncItem[];
 failed: Array<{ item: SyncItem; error: string }>;
}

/** A catalog entry as one dealer sees it. */
export interface PricedProduct {
 id: string;
 code: string;
 name: string;
 description: string;
 category: string;
 billing: Billing;
 unit: string;
 /** Agent: Phoneware list price. Wholesale: the dealer's own default sell price (list until they change it). */
 listPrice: Cents;
 /** Agent only. null for wholesale dealers. */
 floorPrice: Cents | null;
 /** Wholesale only: what the dealer pays Phoneware. null for agent dealers. */
 buyPrice: Cents | null;
 /** false when it cannot be quoted by this dealer right now, with the reason. */
 quotable: boolean;
 notQuotableReason: string | null;
}

// ---------- customers (Autotask-backed) ----------

export type ContactRole = "primary" | "billing" | "technical" | "owner";

export interface CustomerContact {
 autotaskContactId: number;
 name: string;
 email: string | null;
 phone: string | null;
 title: string | null;
 roles: ContactRole[];
}

export interface Address {
 line1: string;
 line2: string | null;
 city: string;
 state: string;
 postalCode: string;
}

export interface Customer {
 /** Autotask company id. Also the id of the ownership record. */
 autotaskCompanyId: number;
 name: string;
 phone: string | null;
 address: Address | null;
 dealerId: string;
 /** User who added it or was assigned it, for `own`-scoped users. */
 ownerEmail: string | null;
 /** Linked OneBill account number, or null when none linked yet. */
 onebillAccountNumber: string | null;
 contacts: CustomerContact[];
 companyType?: number;
 companyNumber?: string | null;
 accountNumber?: string | null;
}

// ---------- quotes ----------

export type QuoteStatus =
 | "draft"
 | "needs_approval"
 | "sent"
 | "viewed"
 | "signed"
 | "ordered"
 | "live"
 | "lost";

export type QuoteType = "new" | "change" | "renewal";

/** Contract term in months, or "month_to_month". */
export type ContractTerm = 12 | 24 | 36 | 60 | "month_to_month" | "coterminous";

export interface QuoteLine {
 productId: string;
 code: string;
 name: string;
 billing: Billing;
 unit: string;
 qty: number;
 unitPrice: Cents;
 /** Snapshots taken when the line was priced, so later catalog edits do not rewrite a sent quote. */
 listPrice: Cents;
 floorPrice: Cents | null;
 buyPrice: Cents | null;
}

export interface ApprovalReason {
 productId: string;
 name: string;
 unitPrice: Cents;
 floorPrice: Cents;
}

export interface Approval {
 required: boolean;
 reasons: ApprovalReason[];
 /** The rep's note explaining the price. */
 requestNote: string | null;
 decision: "approved" | "sent_back" | null;
 decisionNote: string | null;
 decidedBy: string | null;
 decidedAt: Iso | null;
}

export interface QuoteLinkState {
 token: string;
 expiresAt: Iso;
 sentAt: Iso;
 sentTo: string;
 viewedAt: Iso | null;
}

export interface SignatureRecord {
 name: string;
 email: string;
 /** PNG data URL of the drawn signature. */
 drawnPng: string;
 signedAt: Iso;
 ip: string;
 userAgent: string;
 /** SHA-256 hex of the exact PDF bytes the customer was shown. */
 documentSha256: string;
}

export interface Quote {
 id: string;
 /** Human number, "Q-1001". */
 number: string;
 dealerId: string;
 repEmail: string;
 customerId: number;
 customerName: string;
 contactName: string | null;
 contactEmail: string | null;
 type: QuoteType;
 term: ContractTerm;
 status: QuoteStatus;
 lines: QuoteLine[];
 pdfNotes: string;
 internalNotes: string;
 approval: Approval;
 link: QuoteLinkState | null;
 signature: SignatureRecord | null;
 orderId: string | null;
 createdAt: Iso;
 updatedAt: Iso;
 createdVia?: Via | null;
 source?: QuoteSource | null;
 intakeId?: string | null;
}

export interface QuoteTotals {
 monthly: Cents;
 oneTime: Cents;
 /** What the customer pays when they sign: the one-time total. */
 dueAtSigning: Cents;
 /** Agent: how far below list the quote is per month. Wholesale: null. */
 monthlyUnderList: Cents | null;
 /** Wholesale: monthly margin over buy price. Agent: null. */
 monthlyMargin: Cents | null;
}

/** The customer's view of a quote through its link. No internal notes, no buy prices, no floors. */
export interface PublicQuote {
 number: string;
 dealer: { name: string; brand: DealerBrand; phone: string | null };
 customerName: string;
 repName: string;
 type: QuoteType;
 term: ContractTerm;
 lines: Array<Pick<QuoteLine, "name" | "billing" | "unit" | "qty" | "unitPrice">>;
 pdfNotes: string;
 totals: Pick<QuoteTotals, "monthly" | "oneTime" | "dueAtSigning">;
 /** The tax line text. Phase 1: taxes are calculated by billing on the first invoice; no figure. */
 taxNote: string;
 signed: boolean;
 signedBy: string | null;
 signedAt: Iso | null;
 expiresAt: Iso;
}

export const TAX_NOTE_PHASE_1 =
 "Taxes and surcharges are calculated by our billing system on your first invoice.";

// ---------- orders ----------

export type StepState = "pending" | "running" | "done" | "failed";

export interface AutotaskStep {
 state: StepState;
 ticketId: number | null;
 ticketNumber: string | null;
 chargeIds: number[];
 error: string | null;
 attempts: number;
 updatedAt: Iso;
}

export interface OneBillPlacedSubscription {
 subscriptionIdentifier: string;
 pricePlanId: string;
}

export interface OneBillStep {
 state: StepState;
 accountNumber: string | null;
 subscriptionIds: string[];
 placed: Array<{ subscriptionIdentifier: string; pricePlanId: string }>;
 error: string | null;
 attempts: number;
 updatedAt: Iso;
}
export interface CompanyStep {
 state: StepState;
 fromType: number | null;
 error: string | null;
 attempts: number;
 updatedAt: Iso;
}

export type BillingApprovalAccount =
 | { kind: "existing"; accountNumber: string }
 | { kind: "create" };

export interface OrderBillingApproval {
 state: "waiting" | "approved";
 waitingSince: Iso | null;
 account: BillingApprovalAccount | null;
 decidedBy: string | null;
 decidedAt: Iso | null;
 note: string | null;
 /** What ops entered at approval for the mandatory OneBill plan features Omni doesn't fill (#1732). Absent when no line needed any. */
 lineValues?: BillingLineValue[];
}

export type OrderStatus = "in_progress" | "awaiting_billing_approval" | "needs_attention" | "complete";

export interface OrderRunLease {
 token: string;
 until: Iso;
}

export interface Order {
 id: string;
 /** Human number, "O-2001". */
 number: string;
 quoteId: string;
 quoteNumber: string;
 dealerId: string;
 customerId: number;
 customerName: string;
 status: OrderStatus;
 autotask: AutotaskStep;
 onebill: OneBillStep;
 company?: CompanyStep;
 billingApproval?: OrderBillingApproval;
 kind?: OrderKind;
 source?: QuoteSource | null;
 ticketPlan?: "v1" | null;
 tickets?: OrderTicket[];
 notice?: { state: StepState | "sending"; to: string | null; sentAt: Iso | null; error: string | null; attempts: number };
 runLease?: OrderRunLease | null;
 dueDate?: string | null;
 numbersNote?: string;
 dueDateSetBy?: string | null;
 dueDateSetAt?: Iso | null;
 numbersAddedBy?: string | null;
 numbersAddedAt?: Iso | null;
 /** Each line's Telephone Number as the seller set it (#1763), for the lines whose OneBill plan needs one. Ops confirm them at billing approval. */
 lineNumbers?: OrderLineNumber[];
 lineNumbersSetBy?: string | null;
 lineNumbersSetAt?: Iso | null;
 soldBy?: string | null;
 soldAt?: Iso | null;
 createdAt: Iso;
 updatedAt: Iso;
}

export type BillingApprovalRequest = {
 expectedUpdatedAt: Iso;
 note?: string | null;
 /** One value per required feature from GET /api/orders/:id/billing-requirements. Sent only when a line needs one. */
 lineValues?: BillingLineValue[];
} & (
 | {
     kind: "existing";
     accountNumber: string;
     acknowledgeNameMismatch?: boolean;
     acknowledgeSharedAccount?: boolean;
   }
 | {
     kind: "create";
   }
);

export interface AwaitingBillingApprovalOrder extends Order {
 monthly?: Cents;
 oneTime?: Cents;
 billingState: "linked" | "none" | "unreviewed";
 customerBilling: CustomerBilling;
}

/** Tech view of an order waiting on billing approval (no money fields). */
export type TechAwaitingBillingApprovalOrder = Omit<AwaitingBillingApprovalOrder, "monthly" | "oneTime">;

/** Tech view of an order (no price, cost, total, charge amounts). */
export type TechOrder = Order;

// ---------- pure rules ----------

export function lineTotal(line: Pick<QuoteLine, "qty" | "unitPrice">): Cents {
 return line.qty * line.unitPrice;
}

export function quoteTotals(lines: QuoteLine[], model: DealerModel): QuoteTotals {
 let monthly = 0;
 let oneTime = 0;
 let underList = 0;
 let margin = 0;
 for (const l of lines) {
  const t = lineTotal(l);
  if (l.billing === "monthly") {
   monthly += t;
   underList += Math.max(0, l.listPrice - l.unitPrice) * l.qty;
   if (l.buyPrice !== null) margin += (l.unitPrice - l.buyPrice) * l.qty;
  } else {
   oneTime += t;
  }
 }
 return {
  monthly,
  oneTime,
  dueAtSigning: oneTime,
  monthlyUnderList: model === "agent" ? underList : null,
  monthlyMargin: model === "wholesale" ? margin : null,
 };
}

/** Lines priced below their floor. Only agent dealers have floors; wholesale dealers never need approval. */
export function floorViolations(lines: QuoteLine[], model: DealerModel): ApprovalReason[] {
 if (model !== "agent") return [];
 return lines
  .filter((l) => l.floorPrice !== null && l.unitPrice < l.floorPrice)
  .map((l) => ({ productId: l.productId, name: l.name, unitPrice: l.unitPrice, floorPrice: l.floorPrice as Cents }));
}

/** Seed rule decided 2026-10-14: every product's floor starts at 80% of list, rounded to the cent. */
export function seedFloor(listPrice: Cents): Cents {
 return Math.round(listPrice * 0.8);
}

/** Can a quote be sent to the customer right now? */
export function canSend(q: Pick<Quote, "status" | "lines" | "approval">): { ok: true } | { ok: false; reason: string } {
 if (q.lines.length === 0) return { ok: false, reason: "Add at least one product." };
 if (q.status === "signed" || q.status === "ordered" || q.status === "live" || q.status === "lost")
  return { ok: false, reason: "This quote is already closed." };
 if (q.approval.required && q.approval.decision !== "approved")
  return { ok: false, reason: "Phoneware has to approve the price first." };
 return { ok: true };
}

export function formatCents(c: Cents): string {
 const sign = c < 0 ? "-" : "";
 const abs = Math.abs(c);
 return `${sign}$${Math.floor(abs / 100).toLocaleString("en-US")}.${String(abs % 100).padStart(2, "0")}`;
}

export function formatTerm(t: ContractTerm): string {
 if (t === "month_to_month") return "Month to month";
 if (t === "coterminous") return "Coterminous";
 return `${t} months`;
}

// ---------- API contract ----------
// Every route below is served by apps/lens/server under the same origin as the web app.
// Authenticated routes take `Authorization: Bearer <Identity Platform ID token>`.
// Errors are `{ error: string }` with 400 (bad input), 401 (no/invalid token), 403 (not allowed), 404, 409 (state conflict), 502 (Autotask/OneBill failed).

export interface ApiError {
 error: string;
}

export interface PublicConfig {
 /** Identity Platform browser API key and auth domain. Served by GET /api/config so nothing is hard-coded in the bundle. */
 firebaseApiKey: string;
 firebaseAuthDomain: string;
 /** Where the email link lands. */
 emailLinkContinueUrl: string;
}

export interface VersionInfo {
 sha: string;
 builtAt: string;
}

export type InviteRequest = Pick<LensUser, "email" | "name" | "role" | "scope"> & { dealerId: string | null };
/** `autotaskOwnerResourceId` is checked against Autotask (active, not API-only) and stored as `Dealer.autotaskOwner`; null clears it. Ops only. */
export type DealerInput = Pick<Dealer, "name" | "model" | "city" | "brand"> & { autotaskOwnerResourceId: number | null };

/** Explicit association changes preserve each dealer's identity and history. */
export type NsResellerAssociationRequest =
 | { reseller: string; expectedDealerIds: string[] }
 | { reseller: null; expectedReseller: string };

export interface NsResellerAssociationResult {
 dealers: Dealer[];
}
export type CatalogPatch = Partial<Pick<CatalogProduct, "floorPrice" | "wholesaleCost" | "enabled">>;
export interface SyncApplyRequest {
 /** Product ids from a plan the caller reviewed. Only `create` items are applied, and only if the fresh plan still proposes create. */
 productIds: string[];
}
export interface RefreshResult {
 refreshedAt: Iso;
 added: number;
 updated: number;
 deactivated: number;
}
export interface CustomerInput {
 name: string;
 phone: string;
 address: Address;
 contact: { firstName: string; lastName: string; email: string; phone: string | null; title: string | null };
}
export interface AssignCustomerRequest {
 autotaskCompanyId: number;
 dealerId: string;
 ownerEmail: string | null;
 onebillAccountNumber?: string | null;
}
export interface QuoteInput {
 customerId: number;
 type: QuoteType;
 term: ContractTerm;
 lines: Array<Pick<QuoteLine, "productId" | "qty" | "unitPrice">>;
 pdfNotes: string;
 internalNotes: string;
 approvalNote: string | null;
 contactName: string | null;
 contactEmail: string | null;
 source?: QuoteSource | null;
 intakeId?: string | null;
}
export interface SendQuoteRequest {
 to: string;
}
export interface ApprovalDecision {
 decision: "approved" | "sent_back";
 note: string | null;
}
export interface SignRequest {
 name: string;
 email: string;
 drawnPng: string;
 accepted: true;
 /** The SHA-256 the page was shown, from GET /api/q/:token. The server refuses if the document changed. */
 documentSha256: string;
}
export interface PublicQuoteResponse {
 quote: PublicQuote;
 documentSha256: string;
}

// ---------- sync (F2) ----------

export const SYNC_STEP_NAMES = ["catalog", "customers", "billing", "accounts"] as const;
export type SyncStepName = (typeof SYNC_STEP_NAMES)[number];

export interface SyncLease {
 runId: string;
 expiresAt: Iso;
}

export interface SyncStepRunResult {
 outcome: "ok" | "failed";
 error: string | null;
 read: Record<string, number>;
 wrote: Record<string, number>;
 timings?: Record<string, number>;
}

export interface SyncRun {
 id: string;
 trigger: "schedule" | "ops";
 by: string | null;
 startedAt: Iso;
 finishedAt: Iso | null;
 steps: Partial<Record<SyncStepName, SyncStepRunResult>>;
}

export interface CatalogSyncState {
 lastRunId: string | null;
 lastOkAt: Iso | null;
 lastError: string | null;
 autotaskCounts: { products: number; services: number } | null;
 categories?: string[];
 onebillSnapshotAt: Iso | null;
 codes?: CatalogCodeReport | null;
 notInOmni: {
  autotask: Array<{ kind: "product" | "service"; id: number; name: string; period: string | null; active: boolean }>;
  onebill: Array<{
   productCode: string;
   name: string;
   plans: Array<{ id: string; code: string; name: string; billing: Billing | "other"; amount: Cents | null; active: boolean }>;
   candidates: Array<{ productId: string; name: string; score: number; sameCode: boolean }>;
  }>;
 };
}

export interface CustomersSyncState {
 lastRunId: string | null;
 lastOkAt: Iso | null;
 lastError: string | null;
 categories: Array<{ id: number; name: string; nickname: string | null; active: boolean; isDefault: boolean; customers: number }>;
 classifications: Record<string, string>;
 waiting: Array<{ autotaskCompanyId: number; name: string; categoryId: number | null; reason: "no_category" | "category_without_dealer" | "inactive_category" }>;
 counts: { autotaskActive: number; owned: number; waiting: number; differs: number; kept: number };
}

export interface BillingSyncState {
 lastRunId: string | null;
 lastOkAt: Iso | null;
 lastError: string | null;
 counts: { onebillAccounts: number; linked: number; open: number; dismissed: number };
}

export interface SyncStatus {
 inProgress: boolean;
 lease: SyncLease | null;
 lastRun: SyncRun | null;
 catalog: CatalogSyncState | null;
 customers: CustomersSyncState | null;
 billing: BillingSyncState | null;
}

export interface SyncRunRequest {
 steps?: SyncStepName[];
}

/**
 * Route table. Method + path -> request body -> response body.
 *
 * Public
 *   GET  /api/version                       -> VersionInfo
 *   GET  /api/config                        -> PublicConfig
 *   POST /api/auth/email-link  {email}      -> 202 {} (always 202; sends only to invited or active users)
 *   GET  /api/q/:token                      -> PublicQuoteResponse   (marks viewed on first open)
 *   GET  /api/q/:token/pdf                  -> application/pdf (signed copy once signed)
 *   POST /api/q/:token/sign   SignRequest   -> PublicQuoteResponse   (creates the order)
 *
 * Signed in
 *   GET  /api/me                            -> Me
 *   GET  /api/users                         -> LensUser[]    owner: own dealer; ops: all
 *   POST /api/users          InviteRequest  -> LensUser      owner: own dealer, non-ops roles; ops: any
 *   PATCH /api/users/:email  Partial<Pick<LensUser,"name"|"role"|"scope"|"status">> -> LensUser
 *   GET  /api/dealers                       -> Dealer[]      ops: all; others: their own
 *   POST /api/dealers        DealerInput    -> Dealer        ops
 *   PATCH /api/dealers/:id   Partial<DealerInput> -> Dealer  ops; owner may change brand only
 *   GET  /api/dealers/autotask-owners       -> AutotaskOwner[] ops: active Autotask resources that can own a company, by name
 *   GET  /api/dealers/ns-directory          -> NsDirectoryStatus ops: durable directory observation and current coverage
 *   POST /api/dealers/ns-sync               -> NsDealerSyncResult ops: reconcile the complete NS directory
 *   PUT  /api/dealers/:id/ns-reseller NsResellerAssociationRequest -> NsResellerAssociationResult ops: verified, atomic reassignment
 *   GET  /api/catalog                       -> PricedProduct[] for the caller's dealer; ops get CatalogProduct[]
 *   PATCH /api/catalog/:id   CatalogPatch   -> CatalogProduct ops
 *   GET  /api/catalog/:id/onebill-candidates?q= -> OneBillCandidate[] ops: best five by name; with q, search OneBill products by name or code
 *   PUT  /api/catalog/:id/billing-link BillingLinkRequest -> CatalogProduct ops: confirm, create, not billed, or back to unreviewed
 *   POST /api/catalog/refresh               -> RefreshResult ops: pull Autotask services and products into the catalog, keeping Lens's pricing layer and links
 *   POST /api/catalog/sync/plan             -> SyncPlan      ops: what the sync would create and which linked prices differ; writes nothing
 *   POST /api/catalog/sync/apply SyncApplyRequest -> SyncResult ops: create the reviewed `create` items in OneBill and link them
 *   GET  /api/customers?q=                  -> Customer[]    scoped to the caller
 *   POST /api/customers      CustomerInput  -> Customer      creates the Autotask company and contact, owned by the caller's dealer
 *   POST /api/customers/assign AssignCustomerRequest -> Customer ops: give an existing Autotask company to a dealer, optionally linking a OneBill account
 *   GET  /api/customers/:id                 -> Customer      404 outside the caller's scope
 *   POST /api/customers/:id/contacts ContactInput -> CustomerContact scoped to the caller; 409 on duplicate email
 *   GET  /api/quotes?status=                -> Quote[]       scoped to the caller
 *   POST /api/quotes         QuoteInput     -> Quote         draft or needs_approval
 *   GET  /api/quotes/:id                    -> Quote
 *   PUT  /api/quotes/:id     QuoteInput     -> Quote         only while draft or needs_approval or sent back
 *   POST /api/quotes/:id/clone              -> Quote
 *   POST /api/quotes/:id/send SendQuoteRequest -> Quote      emails the QuoteLink; 409 unless canSend
 *   POST /api/quotes/:id/lost               -> Quote
 *   GET  /api/quotes/:id/pdf                -> application/pdf
 *   GET  /api/approvals                     -> Quote[]       ops: quotes needing approval
 *   POST /api/quotes/:id/approval ApprovalDecision -> Quote  ops
 *   GET  /api/orders                        -> Order[]       scoped to the caller
 *   GET  /api/orders/:id                    -> Order
 *   PATCH /api/orders/:id    OrderPatch     -> Order         order scope, until complete: due date, numbers note, each line's Telephone Number
 *                                                            (lineNumbers until billing is approved; 400 naming the line, 502 when OneBill can't say)
 *   POST /api/orders/:id/retry              -> Order         ops: re-run failed steps
 *   GET  /api/orders/:id/billing-requirements -> BillingRequirements order scope: what each line's OneBill plan needs entered; 502 when OneBill can't say
 *   POST /api/orders/:id/billing-approval   -> Order         ops: approve billing account; 400 when a line's value is missing or malformed
 *
 *   GET  /api/sync                          -> SyncStatus    ops
 *   POST /api/sync/runs      SyncRunRequest -> SyncRun       ops
 * Service identity only
 *   POST /api/internal/dealers/ns-sync      -> NsDealerSyncResult dedicated scheduler identity
 *   POST /api/internal/sync                 -> SyncRun       dedicated scheduler identity
 */

// ---------- catalog v2 (Phase 1, #1619) ----------
import type { AutotaskSync } from "./catalog-v2.js";
export * from "./catalog-v2.js";
export type { CatalogPatch as CatalogPatchV2 } from "./catalog-v2.js";
export interface CatalogProduct {
 active?: boolean;
 autotaskSync?: AutotaskSync;
 createdBy?: string | null;
 createdAt?: Iso;
}
export * from "./customers-v2.js";
export * from "./quote-templates.js";
export * from "./via.js";
export * from "./mac.js";
export * from "./quote-intakes.js";
export * from "./intake-formats.js";
export * from "./kqm.js";
export * from "./account-numbers.js";
export * from "./contacts.js";
export * from "./order-notices.js";
export * from "./settings.js";
export * from "./tickets.js";
export * from "./oauth.js";
export * from "./v1-routes.js";
export * from "./mcp-tools.js";
export * from "./openapi.js";
export * from "./billing-requirements.js";
export * from "./act-as.js";
export * from "./feedback.js";
export * from "./accounts.js";
export * from "./sso.js";
